import React, { FC, useCallback, useMemo } from 'react'
import { Box, MenuItem, TableCell, TableRow, Tooltip } from '@mui/material'
import clsx from 'clsx'
import { isEqual, omit } from 'lodash'
import { useTranslation } from 'react-i18next'
import { ThreeDotMenu } from '../../../components'
import { authorizedPersonStatus } from '../../../constants'
import { ContractAuthorizedPersonType } from '../../../graphql'
import { TeamMenuOption, TeamTableRowProps } from '../../../types'
import { dateFormat, rightsTeamRole } from '../../../utils'
import { getFullName } from '../helpers'
import { useWatchDetails } from './hooks'
import { MailPhoneWrap } from './StyledComponents'

const TeamTableListRowComponent: FC<TeamTableRowProps> = ({
  isShouldDisabled,
  teammate,
  teamMenuOptions,
  isViewOnly,
  isMyself,
  hideBorderBottom,
}) => {
  const { t } = useTranslation()

  const {
    id,
    status,
    isAuthorizedSignatory,
    limitedAccessRight,
    signatoryRight,
    groupSize,
    person,
    invitationDate,
  } = teammate as ContractAuthorizedPersonType

  const watchDetails = useWatchDetails(id)

  const menuOptions = useMemo(() => {
    const filteredArr = Object.values(omit(teamMenuOptions, status as string)) as TeamMenuOption[]
    return filteredArr.map((option: TeamMenuOption) => option.label)
  }, [teamMenuOptions])

  const handleMenu = useCallback(() => {
    if (!status || !(status in teamMenuOptions)) return
    const option = teamMenuOptions[status]

    if (option && id) {
      option.handler(id)
    }
  }, [status, teamMenuOptions, id])

  return (
    <TableRow
      hover
      sx={{
        '& td': {
          borderBottom: hideBorderBottom ? 'none' : '',
        },
        cursor: 'pointer',
      }}
      data-test="teammateItem"
      onClick={watchDetails}
    >
      <TableCell>
        <Box width={250}>{getFullName(person)}</Box>
      </TableCell>
      <TableCell>
        <Box overflow="hidden" whiteSpace="normal" width={180}>
          {isAuthorizedSignatory
            ? rightsTeamRole(signatoryRight as string, groupSize ?? 0)
            : rightsTeamRole(limitedAccessRight as string)}
        </Box>
      </TableCell>

      <TableCell>
        <Box width={180}>
          <MailPhoneWrap>
            <a href={`mailto:${person?.email}`} onClick={(e) => e.stopPropagation()}>
              {person?.email}
            </a>
          </MailPhoneWrap>
        </Box>
      </TableCell>

      <TableCell data-test="invitedDate">
        <Box width={160}>{invitationDate ? dateFormat(invitationDate) : t('never', 'Never')}</Box>
      </TableCell>
      <TableCell>
        <Box width={160}>
          {person?.lastSeenAt ? dateFormat(person?.lastSeenAt) : t('never', 'Never')}
        </Box>
      </TableCell>
      <TableCell>
        <Box width={140} className={clsx('roundedPill', authorizedPersonStatus[status as string])}>
          {t(authorizedPersonStatus[status as string])}
        </Box>
      </TableCell>
      <TableCell>
        {!isViewOnly && (
          <ThreeDotMenu disabled={isShouldDisabled}>
            {menuOptions.map((option, index) =>
              isMyself && option === 'Deactivate' ? (
                <Tooltip
                  key={index}
                  title={t('cantDeactYourslf', 'You cannot deactivate yourself')}
                >
                  <MenuItem key={index} onClick={handleMenu} disabled>
                    {t(option)}
                  </MenuItem>
                </Tooltip>
              ) : (
                <MenuItem key={index} onClick={handleMenu}>
                  {t(option)}
                </MenuItem>
              ),
            )}
          </ThreeDotMenu>
        )}
      </TableCell>
    </TableRow>
  )
}

export const TeamTableListRow = React.memo(TeamTableListRowComponent, isEqual)
